import {z as zod} from "zod";
import type {OrganizationalUnit} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnit";
import type {User} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {iso8601DateTimeZodValidator, uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {uniqueUuid} from "@vesta/unique/unique";

/** Segment in OttopilotDataViewer query keys for the BTL activities index route. */
export const BTL_ACTIVITIES_OTTOPILOT_VIEWER_INSTANCE_ID = uniqueUuid`efadf6c4-78b9-4e6e-b474-0f8c46f661bf`;

export enum BtlActivityTemplates {
    doorToDoor = "573d1c34-5324-4f6f-9fb7-87faffe3a422",
    leadCaptureBooth = "5100b875-1837-4697-a3ec-54bf1da081e9",
    roadshow = "39464ada-e4a4-4b5d-89ef-aac4f6872edb",
    referralDriveCampaign = "8268ef89-bde7-418b-b1d7-d7acea459bf7",
    dealerFootfallBooster = "9cb987f0-d21d-4554-b62b-34b4da47e631",
}

export enum AssignmentMode {
    auto = "93c004a3-3690-4981-abb1-186da5d82e47",
    manual = "d99c1dc7-8744-4a3e-82fd-ed8b869042b4",
}

export enum BtlActivityStatus {
    scheduled = "49268234-2c3b-4456-baee-e2d8fc2fa437",
    cancelled = "c55c6e6d-7400-436f-914a-910f7caacd03",
    completed = "b9f36da7-f8ae-4c6b-b84d-9f2b1448cdc2",
    draft = "37219b57-42f9-4678-81d2-2c24e5102c39",
    active = "b138777b-5564-4046-9abe-c42bb6fc6730",
}

export enum BtlReviewStatus {
    valid = "valid",
    invalid = "invalid",
    warning = "warning",
}

export type BtlActivityGalleryItem = {
    assetId: string;
    fileName: string;
    uploadedAt: Iso8601DateTime;
};

/**
 * A single uploaded file within a checklist response.
 * `assetId` is the UUID returned by the assets upload API.
 * `fileName` is the original file name (for display).
 */
export type BtlChecklistMediaItem = {
    assetId: Uuid;
    fileName: string;
    uploadedAt: Iso8601DateTime;
};

/**
 * One user's response to a single checklist task item.
 * Matches the wiring diagram: { textInput, media: Array<Files> }
 * (media is stored as resolved asset metadata after upload).
 */
export type BtlChecklistResponseEntry = {
    id: Uuid;
    /** UUID of the user who posted this response */
    userId: Uuid;
    /** Timestamp the response was created */
    createdAt: Iso8601DateTime;
    /** Free-text comment / observation */
    textInput: string;
    /** Uploaded media assets (photos, docs, etc.) */
    media: Array<BtlChecklistMediaItem>;
};

/**
 * Full map of checklist responses for a BTL activity.
 * Keyed by `{checklistId}:{itemId}` - a composite key that
 * uniquely identifies each task within the activity's template.
 *
 * Stored under the checklist response payload on the backend.
 */
export type BtlActivityChecklistResponses = Array<{
    checklistId: Uuid;
    checklistItemId: Uuid;
    responses: Array<BtlChecklistResponseEntry>;
}>;

export type BtlActivityApiType = {
    id: Uuid;
    templateId: Uuid;
    createdBy: Uuid;
    createdAt: Iso8601DateTime;
    updatedBy: Uuid;
    updatedAt: Iso8601DateTime;
    btlStatus: BtlActivityStatus;
    status: Uuid;
    title: string;
    description: string;
    timezone: Uuid;
    startDate: Iso8601DateTime;
    endDate: Iso8601DateTime;
    isAlldayEvent: boolean;
    assignmentMode: AssignmentMode;
    eventOwner?: Uuid | null;
    assignedUsers: Array<Uuid>;
    executionRole?: Uuid | null;
    geographyNodes: Array<Uuid>;
    gallery?: unknown;
    /** Raw checklist responses from backend - normalized on conversion. */
    checklistResponses?: unknown;
    checklistCompletionDetails?: unknown;
};

export type BtlActivity = {
    id: Uuid;
    btlActivityTemplateId: Uuid;
    createdBy: Uuid;
    createdAt: Iso8601DateTime;
    updatedBy: Uuid;
    updatedAt: Iso8601DateTime;
    btlStatus: BtlActivityStatus;
    title: string;
    description: string;
    timezone: Uuid;
    startDate: Iso8601DateTime;
    endDate: Iso8601DateTime;
    isAlldayEvent: boolean;
    assignmentMode: AssignmentMode;
    assignedUsers: Array<{
        userTemplateId: Uuid;
        userIds: Array<Uuid>;
    }>;
    executionRole: Uuid | null;
    geographyNodes: Array<{
        ouTemplateId: Uuid;
        ouIds: Array<Uuid>;
    }>;
    gallery: Array<BtlActivityGalleryItem>;
    /** Indexed checklist task responses for this event. Key = `${checklistId}:${itemId}`. */
    checklistResponses: BtlActivityChecklistResponses;
};

export function btlActivityTemplateToDisplayName(btlActivityTemplate: Uuid): string {
    switch (btlActivityTemplate as unknown as BtlActivityTemplates) {
        case BtlActivityTemplates.doorToDoor: {
            return "Door to Door";
        }
        case BtlActivityTemplates.leadCaptureBooth: {
            return "Lead Capture Booth";
        }
        case BtlActivityTemplates.roadshow: {
            return "Roadshow";
        }
        case BtlActivityTemplates.referralDriveCampaign: {
            return "Referral Drive Campaign";
        }
        case BtlActivityTemplates.dealerFootfallBooster: {
            return "Dealer Footfall Booster";
        }
        default: {
            console.error("Invalid btlActivityTemplate", btlActivityTemplate);
            return "Unknown";
        }
    }
}

export function assignmentModeToDisplayName(assignmentMode: AssignmentMode): string {
    switch (assignmentMode) {
        case AssignmentMode.auto: {
            return "Auto";
        }
        case AssignmentMode.manual: {
            return "Manual";
        }
        default: {
            console.error("Invalid assignmentMode", assignmentMode);
            // throw new Error("Invalid contestStatus");
            return "Unknown";
        }
    }
}

export function btlEventStatusToDisplayName(btlEventStatus: BtlActivityStatus): string {
    switch (btlEventStatus) {
        case BtlActivityStatus.scheduled: {
            return "Scheduled";
        }
        case BtlActivityStatus.cancelled: {
            return "Cancelled";
        }
        case BtlActivityStatus.completed: {
            return "Completed";
        }
        case BtlActivityStatus.draft: {
            return "Draft";
        }
        case BtlActivityStatus.active: {
            return "Live";
        }
        default: {
            console.error("Invalid btlEventStatus", btlEventStatus);
            // throw new Error("Invalid contestStatus");
            return "Unknown";
        }
    }
}

/** UTC end-of-day (23:59:59.999) for the calendar day of `iso`. */
export function getEndOfUtcCalendarDayMs(iso: Iso8601DateTime): number {
    const d = new Date(iso);
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 23, 59, 59, 999);
}

/**
 * Latest timestamp when the host may still press Start: `startDate + eventStartBufferMs`, or end of the UTC
 * calendar day of `startDate` when buffer is omitted - always capped by `endDate`.
 */
export function getBtlEventStartWindowEndMs(args: {startDate: Iso8601DateTime; endDate: Iso8601DateTime; eventStartBufferMs?: number}): number {
    const startMs = new Date(args.startDate).getTime();
    const endMs = new Date(args.endDate).getTime();
    const bufferEnd = args.eventStartBufferMs !== undefined ? startMs + args.eventStartBufferMs : getEndOfUtcCalendarDayMs(args.startDate);
    return Math.min(bufferEnd, endMs);
}

export function canStartBtlEventInWindow(args: {row: BtlActivity; eventStartBufferMs?: number; now?: Date}): boolean {
    const nowMs = (args.now ?? new Date()).getTime();
    const {row} = args;
    if (row.btlStatus !== BtlActivityStatus.scheduled && row.btlStatus !== BtlActivityStatus.draft) {
        return false;
    }
    const startMs = new Date(row.startDate).getTime();
    const endMs = new Date(row.endDate).getTime();
    if (nowMs < startMs || nowMs > endMs) {
        return false;
    }
    const latestStart = getBtlEventStartWindowEndMs({
        startDate: row.startDate,
        endDate: row.endDate,
        eventStartBufferMs: args.eventStartBufferMs,
    });
    return nowMs <= latestStart;
}

export type BtlActivityChecklistResponsesByKey = Record<string, Array<BtlChecklistResponseEntry>>;

export function btlActivityChecklistResponsesToByKey(checklistResponses: BtlActivityChecklistResponses): BtlActivityChecklistResponsesByKey {
    const out: BtlActivityChecklistResponsesByKey = {};
    for (const group of checklistResponses) {
        const key = `${group.checklistId}:${group.checklistItemId}`;
        out[key] = group.responses;
    }
    return out;
}

export function btlActivityChecklistResponsesFromByKey(byKey: BtlActivityChecklistResponsesByKey): BtlActivityChecklistResponses {
    const out: BtlActivityChecklistResponses = [];
    for (const [key, responses] of Object.entries(byKey)) {
        const [checklistId, checklistItemId] = key.split(":");
        if (typeof checklistId !== "string" || typeof checklistItemId !== "string") {
            continue;
        }
        out.push({
            checklistId: checklistId as Uuid,
            checklistItemId: checklistItemId as Uuid,
            responses,
        });
    }
    return out;
}

/** Transforms API `gallery` payloads into `BtlActivityGalleryItem[]`. */
export function transformBtlActivityGalleryItems(raw: unknown): Array<BtlActivityGalleryItem> {
    let normalizedRaw: unknown = raw;
    if (typeof normalizedRaw === "string") {
        const trimmed = normalizedRaw.trim();
        // Some backends return gallery as serialized JSON.
        if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
            try {
                normalizedRaw = JSON.parse(trimmed) as unknown;
            } catch {
                // Keep original value and fall through to other guards.
            }
        }
    }

    if (normalizedRaw === null || normalizedRaw === undefined || Array.isArray(normalizedRaw) === false) {
        return [];
    }
    const fallbackTime = new Date().toISOString() as Iso8601DateTime;
    return normalizedRaw
        .map((item, index): BtlActivityGalleryItem | null => {
            if (typeof item === "string") {
                return {
                    assetId: item as Uuid,
                    fileName: `Media ${index + 1}`,
                    uploadedAt: fallbackTime,
                };
            }
            if (typeof item !== "object" || item === null) {
                return null;
            }
            const o = item as Record<string, unknown>;
            const idRaw = o.assetId ?? o.asset_id ?? o.id;
            if (typeof idRaw !== "string") {
                return null;
            }
            const rawFileName = o.fileName ?? o.file_name;
            const fileName = typeof rawFileName === "string" ? rawFileName : `Media ${index + 1}`;
            const uploadedRaw = o.uploadedAt ?? o.uploaded_at;
            const uploadedAt = typeof uploadedRaw === "string" && uploadedRaw.length > 5 ? uploadedRaw : fallbackTime;

            return {
                assetId: idRaw as Uuid,
                fileName,
                uploadedAt: uploadedAt as Iso8601DateTime,
            };
        })
        .filter((x): x is BtlActivityGalleryItem => x !== null);
}

/** Transforms raw checklist payload into finalized checklist response array type. */
export function transformBtlActivityChecklistResponses(raw: unknown): BtlActivityChecklistResponses {
    if (raw === null || raw === undefined) {
        return [];
    }

    let parsedRaw: unknown = raw;
    if (typeof parsedRaw === "string") {
        const trimmed = parsedRaw.trim();
        if (trimmed.length === 0 || trimmed === "{}") {
            return [];
        }
        if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
            try {
                parsedRaw = JSON.parse(trimmed) as unknown;
            } catch {
                return [];
            }
        }
    }

    if (Array.isArray(parsedRaw)) {
        const out: BtlActivityChecklistResponses = [];
        for (const group of parsedRaw) {
            if (typeof group !== "object" || group === null) {
                continue;
            }
            const g = group as Record<string, unknown>;
            if (typeof g.checklistId !== "string" || typeof g.checklistItemId !== "string" || Array.isArray(g.responses) === false) {
                continue;
            }
            out.push({
                checklistId: g.checklistId as Uuid,
                checklistItemId: g.checklistItemId as Uuid,
                responses: g.responses as Array<BtlChecklistResponseEntry>,
            });
        }
        return out;
    }

    if (typeof parsedRaw !== "object") {
        return [];
    }

    const byKey: BtlActivityChecklistResponsesByKey = {};
    const fallbackTime = new Date().toISOString() as Iso8601DateTime;
    for (const [key, entries] of Object.entries(parsedRaw as Record<string, unknown>)) {
        if (Array.isArray(entries) === false) {
            continue;
        }
        byKey[key] = entries
            .map((entry, index): BtlChecklistResponseEntry | null => {
                if (typeof entry !== "object" || entry === null) {
                    return null;
                }
                const e = entry as Record<string, unknown>;
                const rawUserId = (e.userId ?? e.user_id) as string | undefined;
                const rawCreatedAt = (e.createdAt ?? e.created_at) as string | undefined;
                const rawTextInput = (e.textInput ?? e.text_input) as string | undefined;
                return {
                    id: (typeof e.id === "string" && e.id.length > 0 ? e.id : `${key}:${index}`) as Uuid,
                    userId: (typeof rawUserId === "string" ? rawUserId : "") as Uuid,
                    createdAt: (typeof rawCreatedAt === "string" && rawCreatedAt.length > 0 ? rawCreatedAt : fallbackTime) as Iso8601DateTime,
                    textInput: typeof rawTextInput === "string" ? rawTextInput : "",
                    media: Array.isArray(e.media)
                        ? e.media
                              .map((m): BtlChecklistMediaItem | null => {
                                  if (typeof m !== "object" || m === null) {
                                      return null;
                                  }
                                  const item = m as Record<string, unknown>;
                                  const rawId = (item.assetId ?? item.asset_id) as string | undefined;
                                  if (typeof rawId !== "string" || rawId.length === 0) {
                                      return null;
                                  }
                                  const rawFn = (item.fileName ?? item.file_name) as string | undefined;
                                  const rawAt = (item.uploadedAt ?? item.uploaded_at) as string | undefined;
                                  return {
                                      assetId: rawId as Uuid,
                                      fileName: typeof rawFn === "string" ? rawFn : "",
                                      uploadedAt: (typeof rawAt === "string" && rawAt.length > 0 ? rawAt : fallbackTime) as Iso8601DateTime,
                                  };
                              })
                              .filter((m): m is BtlChecklistMediaItem => m !== null)
                        : [],
                };
            })
            .filter((entry): entry is BtlChecklistResponseEntry => entry !== null);
    }
    return btlActivityChecklistResponsesFromByKey(byKey);
}

/** Full update body (camelCase) so partial UI edits do not drop other event fields. */
export function buildBtlActivityUpdateApiData(
    row: BtlActivity,
    gallery: Array<BtlActivityGalleryItem>,
): {
    btl_status: BtlActivityStatus;
    end_date: Iso8601DateTime;
    timezone: Uuid;
    description: string;
    geography_nodes: Array<Uuid>;
    title: string;
    assigned_users: Array<Uuid>;
    execution_role: Uuid | null;
    is_allday_event: boolean;
    assignment_mode: AssignmentMode;
    start_date: Iso8601DateTime;
    gallery: Array<{asset_id: string; file_name: string; uploaded_at: Iso8601DateTime}>;
    checklist_completion_details: BtlActivityChecklistResponsesByKey;
} {
    return {
        btl_status: row.btlStatus,
        end_date: row.endDate,
        timezone: row.timezone,
        description: row.description,
        geography_nodes: row.geographyNodes.flatMap((n) => n.ouIds),
        title: row.title,
        assigned_users: row.assignedUsers.flatMap((g) => g.userIds),
        execution_role: row.executionRole,
        is_allday_event: row.isAlldayEvent,
        assignment_mode: row.assignmentMode,
        start_date: row.startDate,
        gallery: gallery.map((g) => ({
            asset_id: g.assetId,
            file_name: g.fileName,
            uploaded_at: g.uploadedAt,
        })),
        checklist_completion_details: btlActivityChecklistResponsesToByKey(row.checklistResponses),
    };
}

export function buildBtlChecklistResponsesUpdateApiData(checklistResponses: BtlActivityChecklistResponses): {
    checklistCompletionDetails: BtlActivityChecklistResponsesByKey;
} {
    return {
        checklistCompletionDetails: btlActivityChecklistResponsesToByKey(checklistResponses),
    };
}

export function buildBtlChecklistResponsesFromByKeyUpdateApiData(checklistResponses: BtlActivityChecklistResponsesByKey): {
    checklistCompletionDetails: BtlActivityChecklistResponsesByKey;
} {
    return {
        checklistCompletionDetails: checklistResponses,
    };
}

export function convertBtlActivityApiTypeToBtlActivity({
    apiEvent,
    users,
    organizationalUnits,
}: {
    apiEvent: BtlActivityApiType;
    users: Array<User>;
    organizationalUnits: Array<OrganizationalUnit>;
}): BtlActivity {
    // Group assigned users by their user template ID
    const assignedUsersGrouped = apiEvent.assignedUsers.reduce<Array<{userTemplateId: Uuid; userIds: Array<Uuid>}>>((acc, userId) => {
        const user = users.find((u) => u.id === userId);
        if (user === undefined) {
            return acc;
        }

        const existingGroup = acc.find((g) => g.userTemplateId === user.templateId);
        if (existingGroup !== undefined) {
            existingGroup.userIds.push(userId);
        } else {
            acc.push({
                userTemplateId: user.templateId,
                userIds: [userId],
            });
        }
        return acc;
    }, []);

    // Group geography nodes by their OU template ID
    const geographyNodesGrouped = apiEvent.geographyNodes.reduce<Array<{ouTemplateId: Uuid; ouIds: Array<Uuid>}>>((acc, ouId) => {
        const ou = organizationalUnits.find((o) => o.id === ouId);
        if (ou === undefined) {
            return acc;
        }

        const existingGroup = acc.find((g) => g.ouTemplateId === ou.templateId);
        if (existingGroup !== undefined) {
            existingGroup.ouIds.push(ouId);
        } else {
            acc.push({
                ouTemplateId: ou.templateId,
                ouIds: [ouId],
            });
        }
        return acc;
    }, []);

    return {
        id: apiEvent.id,
        btlActivityTemplateId: apiEvent.templateId,
        createdBy: apiEvent.createdBy,
        createdAt: apiEvent.createdAt,
        updatedBy: apiEvent.updatedBy,
        updatedAt: apiEvent.updatedAt,
        btlStatus: apiEvent.btlStatus,
        title: apiEvent.title,
        description: apiEvent.description,
        timezone: apiEvent.timezone,
        startDate: apiEvent.startDate,
        endDate: apiEvent.endDate,
        isAlldayEvent: apiEvent.isAlldayEvent,
        assignmentMode: apiEvent.assignmentMode,
        assignedUsers: assignedUsersGrouped,
        executionRole: apiEvent.executionRole ?? null,
        geographyNodes: geographyNodesGrouped,
        gallery: transformBtlActivityGalleryItems(apiEvent.gallery),
        checklistResponses: transformBtlActivityChecklistResponses(apiEvent.checklistCompletionDetails ?? apiEvent.checklistResponses),
    };
}

// Raw API response item from CSV upload endpoint.
// Each field key can appear either in errors (validation message) or in btlActivity (valid value).
export type BtlReviewItemApiResponseItem = {
    errors: Record<string, string>;
    btlActivity: {
        btlStatus: BtlActivityStatus;
        templateId: BtlActivityTemplates;
        isAlldayEvent?: boolean;
        startDate?: Iso8601DateTime;
        endDate?: Iso8601DateTime;
        assignmentMode?: Uuid;
        timezone?: Uuid;
        description?: string;
        assignedUsers: Array<Uuid>;
        title?: string;
        geographyNodes?: Array<Uuid>;
    };
};

// Zod schema for BtlReviewItemApiResponseItem
export const btlReviewItemApiResponseItemSchema = zod.object({
    errors: zod.record(zod.string(), zod.string()).optional(),
    btlActivity: zod
        .object({
            btlStatus: zod.enum(BtlActivityStatus).optional(),
            templateId: zod.enum(BtlActivityTemplates).optional(),
            isAlldayEvent: zod.boolean().optional(),
            startDate: zod.string().optional(),
            endDate: zod.string().optional(),
            assignmentMode: zod.string().optional(),
            timezone: zod.string().optional(),
            description: zod.string().optional(),
            assignedUsers: zod.array(uuidZodValidator),
            title: zod.string().optional(),
            geographyNodes: zod.array(zod.string()).optional(),
        })
        .optional(),
});

// Zod schema for array of BtlReviewItemApiResponseItem
export const btlReviewItemApiResponseArraySchema = zod.array(btlReviewItemApiResponseItemSchema);

// Normalized type for internal use
export type BtlReviewItemApiType = {
    id: Uuid;
    title: string;
    btlActivityTemplateId: BtlActivityTemplates | null;
    validationStatus: BtlReviewStatus;
    validationErrors: Array<string>;
    assignedUsers: Array<Uuid>;
    isAlldayEvent: boolean;
    startDate: Iso8601DateTime;
    startTime?: string;
    endDate: Iso8601DateTime;
    endTime?: string;
    region: Array<Uuid>;
    atoll: Array<Uuid>;
    island: Array<Uuid>;
    description: string;
    timezone: Uuid;
    assignmentMode: Uuid;
};

export type BtlReviewItem = {
    id: Uuid;
    title: string;
    btlActivityTemplateId: BtlActivityTemplates | null;
    validationStatus: BtlReviewStatus;
    validationErrors: Array<string>;
    assignedUsers: Array<{
        userTemplateId: Uuid;
        userIds: Array<Uuid>;
    }>;
    isAlldayEvent: boolean;
    startDate: Iso8601DateTime;
    startTime?: string;
    endDate: Iso8601DateTime;
    endTime?: string;
    region: Array<Uuid>;
    atoll: Array<Uuid>;
    island: Array<Uuid>;
    description: string;
    timezone: Uuid;
    assignmentMode: Uuid;
};

export function btlReviewStatusToDisplayName(reviewStatus: BtlReviewStatus): string {
    switch (reviewStatus) {
        case BtlReviewStatus.valid: {
            return "Valid";
        }
        case BtlReviewStatus.invalid: {
            return "Invalid";
        }
        case BtlReviewStatus.warning: {
            return "Warning";
        }
        default: {
            console.error("Invalid reviewStatus", reviewStatus);
            return "Unknown";
        }
    }
}

export function convertBtlReviewItemApiTypeToBtlReviewItem({apiItem, users}: {apiItem: BtlReviewItemApiType; users: Array<User>}): BtlReviewItem {
    // Group assigned users by their user template ID
    const assignedUsersGrouped = apiItem.assignedUsers.reduce<Array<{userTemplateId: Uuid; userIds: Array<Uuid>}>>((acc, userId) => {
        const user = users.find((u) => u.id === userId);
        if (user === undefined) {
            return acc;
        }

        const existingGroup = acc.find((g) => g.userTemplateId === user.templateId);
        if (existingGroup !== undefined) {
            existingGroup.userIds.push(userId);
        } else {
            acc.push({
                userTemplateId: user.templateId,
                userIds: [userId],
            });
        }
        return acc;
    }, []);

    return {
        id: apiItem.id,
        title: apiItem.title,
        description: apiItem.description,
        btlActivityTemplateId: apiItem.btlActivityTemplateId,
        validationStatus: apiItem.validationStatus,
        validationErrors: apiItem.validationErrors,
        assignedUsers: assignedUsersGrouped,
        isAlldayEvent: apiItem.isAlldayEvent,
        startDate: apiItem.startDate,
        startTime: apiItem.startTime,
        endDate: apiItem.endDate,
        endTime: apiItem.endTime,
        timezone: apiItem.timezone,
        assignmentMode: apiItem.assignmentMode,
        region: apiItem.region,
        atoll: apiItem.atoll,
        island: apiItem.island,
    };
}

export const btlActivityApiTypeSchema = zod.object({
    id: uuidZodValidator,
    templateId: uuidZodValidator,
    btlStatus: zod.nativeEnum(BtlActivityStatus),
    createdBy: uuidZodValidator,
    createdAt: iso8601DateTimeZodValidator,
    updatedBy: uuidZodValidator,
    updatedAt: iso8601DateTimeZodValidator,
    status: uuidZodValidator,
    title: zod.string(),
    description: zod.string(),
    timezone: uuidZodValidator,
    startDate: iso8601DateTimeZodValidator,
    endDate: iso8601DateTimeZodValidator,
    isAlldayEvent: zod.boolean(),
    assignmentMode: zod.nativeEnum(AssignmentMode),
    assignedUsers: zod.array(uuidZodValidator),
    executionRole: zod.union([uuidZodValidator, zod.null()]).optional(),
    geographyNodes: zod.array(uuidZodValidator),
    gallery: zod.unknown().optional(),
    checklistResponses: zod.unknown().optional(),
    eventOwner: uuidZodValidator.optional().nullable(),
    checklistCompletionDetails: zod.unknown().optional(),
});

// Zod schema for array of BtlActivityApiType
export const btlActivityApiTypeArraySchema = zod.array(btlActivityApiTypeSchema);
